import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { checkinRedemptionApi, dropinApi, isApiError } from "@bw/shared/api";
import type {
  CheckinPassResponse,
  DropInSessionResponse,
  RedemptionPass,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import {
  useOnline,
  usePageVisible,
  useScreenWakeLock,
} from "../../../common/hooks/deviceState";
import {
  REDEMPTION_CHECKED_IN_POLL_MS,
  REDEMPTION_PASS_USABLE_MS,
  REDEMPTION_REFRESH_MS,
  REDEMPTION_RETRY_MS,
  type RedemptionEndReason,
} from "../lib/redemption";

export interface RedemptionSummary {
  redemptionId: string;
  kind: RedemptionPass["kind"];
  planName: string;
  /** Visits left before this one is used; null for an unlimited membership. */
  unitsLeft: number | null;
  /** The intent's server expiry (10 minutes after "Redeem one"). */
  expiresAt: string;
}

export type RedemptionCodeState =
  | { status: "starting" }
  | {
      status: "showing";
      redemption: RedemptionSummary;
      pass: CheckinPassResponse;
      /** Client time the current pass arrived. */
      passReceivedAt: number;
      /** Client time the next refresh is due. */
      refreshDueAt: number;
    }
  | {
      status: "checked_in";
      redemption: RedemptionSummary;
      session: DropInSessionResponse;
    }
  | {
      status: "ended";
      redemption: RedemptionSummary | null;
      reason: RedemptionEndReason;
    }
  | { status: "failed"; error: unknown };

type Action =
  | { type: "start" }
  | { type: "created"; redemption: RedemptionSummary; pass: CheckinPassResponse; now: number }
  | { type: "refreshed"; pass: CheckinPassResponse; now: number }
  | { type: "refresh_failed"; now: number }
  | { type: "checked_in"; session: DropInSessionResponse }
  | { type: "ended"; reason: RedemptionEndReason }
  | { type: "failed"; error: unknown };

function reducer(state: RedemptionCodeState, action: Action): RedemptionCodeState {
  switch (action.type) {
    case "start":
      return { status: "starting" };
    case "created":
      return {
        status: "showing",
        redemption: action.redemption,
        pass: action.pass,
        passReceivedAt: action.now,
        refreshDueAt: action.now + REDEMPTION_REFRESH_MS,
      };
    case "refreshed":
      if (state.status !== "showing") return state;
      return {
        ...state,
        pass: action.pass,
        passReceivedAt: action.now,
        refreshDueAt: action.now + REDEMPTION_REFRESH_MS,
      };
    case "refresh_failed":
      if (state.status !== "showing") return state;
      return { ...state, refreshDueAt: action.now + REDEMPTION_RETRY_MS };
    case "checked_in":
      if (state.status !== "showing" && state.status !== "ended") return state;
      if (!state.redemption) return state;
      return { status: "checked_in", redemption: state.redemption, session: action.session };
    case "ended":
      if (state.status !== "showing") return state;
      return { status: "ended", redemption: state.redemption, reason: action.reason };
    case "failed":
      return { status: "failed", error: action.error };
  }
}

function summarize(created: RedemptionPass): RedemptionSummary {
  return {
    redemptionId: created.redemption_id,
    kind: created.kind,
    planName: created.plan_name,
    unitsLeft: created.units_left ?? null,
    expiresAt: created.expires_at,
  };
}

/** What the server says became of the code, once it is no longer open. */
type CodeOutcome =
  | { kind: "checked_in"; session: DropInSessionResponse }
  | { kind: "ended"; reason: RedemptionEndReason };

/**
 * The "Redeem one" check-in code for one pass at one facility.
 *
 * - Opens the redemption once on mount (the server returns the same open
 *   intent for the same pass, so a remount never stacks intents).
 * - Replaces the 40 s pass every 30 s while the page is visible and online.
 *   A hidden page stops refreshing; becoming visible refreshes at once if the
 *   next refresh is already due.
 * - Stops at the intent's 10-minute expiry ("Get a new code" starts again).
 * - Asks the server what became of the code (GET the redemption) every few
 *   seconds, so the scan's check-in shows up on the phone quickly, and once
 *   when a refresh finds the code gone. Consumed shows the drop-in session it
 *   admitted; released (the kiosk refused it) and expired show the end.
 */
export function useRedemptionCode({
  facilityId,
  subscriptionId,
}: {
  facilityId: string;
  subscriptionId: string;
}) {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(reducer, { status: "starting" });
  const visible = usePageVisible();
  const online = useOnline();
  const [now, setNow] = useState(() => Date.now());
  const generation = useRef(0);
  const refreshing = useRef(false);

  const showing = state.status === "showing" ? state : null;
  const redemption =
    state.status === "showing" || state.status === "checked_in"
      ? state.redemption
      : state.status === "ended"
        ? state.redemption
        : null;
  const redemptionId = showing?.redemption.redemptionId ?? null;
  const intentExpiresAt = showing?.redemption.expiresAt ?? null;
  const refreshDueAt = showing?.refreshDueAt ?? null;

  useScreenWakeLock(state.status === "showing");

  const start = useCallback(() => {
    const run = ++generation.current;
    dispatch({ type: "start" });
    checkinRedemptionApi
      .create({ facility_id: facilityId, subscription_id: subscriptionId })
      .then((created) => {
        if (run !== generation.current) return;
        dispatch({
          type: "created",
          redemption: summarize(created),
          pass: created.pass,
          now: Date.now(),
        });
      })
      .catch((error: unknown) => {
        if (run !== generation.current) return;
        dispatch({ type: "failed", error });
      });
  }, [facilityId, subscriptionId]);

  useEffect(() => {
    start();
    return () => {
      generation.current += 1;
    };
  }, [start]);

  const markCheckedIn = useCallback(
    (session: DropInSessionResponse) => {
      dispatch({ type: "checked_in", session });
      queryClient.setQueryData(queryKeys.dropin.session(facilityId), session);
      void queryClient.invalidateQueries({ queryKey: queryKeys.dropin.paymentOptions(facilityId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dropin.currentSessions() });
    },
    [facilityId, queryClient],
  );

  const outcomeOf = useCallback(
    async (id: string): Promise<CodeOutcome | null> => {
      const status = await checkinRedemptionApi.status(id);
      if (status.state === "open") return null;
      if (status.state === "consumed") {
        const session = await dropinApi.getSession(facilityId).catch(() => null);
        if (session && session.session_id === status.session_id) {
          return { kind: "checked_in", session };
        }
        // Used, but that admission is no longer current (the desk undid it,
        // which gives the visit back, or it already ended).
        return { kind: "ended", reason: "ended" };
      }
      return { kind: "ended", reason: status.state };
    },
    [facilityId],
  );

  const apply = useCallback(
    (outcome: CodeOutcome) => {
      if (outcome.kind === "checked_in") markCheckedIn(outcome.session);
      else dispatch({ type: "ended", reason: outcome.reason });
    },
    [markCheckedIn],
  );

  // Refresh the pass when it is due, only while visible and online.
  useEffect(() => {
    if (!redemptionId || !intentExpiresAt || refreshDueAt === null) return;
    if (!visible || !online) return;
    const run = generation.current;
    const timer = window.setTimeout(
      () => {
        if (refreshing.current) return;
        if (Date.now() >= Date.parse(intentExpiresAt)) {
          dispatch({ type: "ended", reason: "expired" });
          return;
        }
        refreshing.current = true;
        checkinRedemptionApi
          .refreshPass(redemptionId)
          .then((pass) => {
            if (run === generation.current) dispatch({ type: "refreshed", pass, now: Date.now() });
          })
          .catch(async (error: unknown) => {
            if (run !== generation.current) return;
            if (
              isApiError(error) &&
              (error.status === 404 || error.code === "redemption_used")
            ) {
              // The code is no longer open (or a scan used it): ask what
              // became of it.
              const outcome = await outcomeOf(redemptionId).catch(() => null);
              if (run !== generation.current) return;
              apply(outcome ?? { kind: "ended", reason: "ended" });
              return;
            }
            dispatch({ type: "refresh_failed", now: Date.now() });
          })
          .finally(() => {
            refreshing.current = false;
          });
      },
      Math.max(0, refreshDueAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [apply, intentExpiresAt, online, outcomeOf, redemptionId, refreshDueAt, visible]);

  // The intent ends after 10 minutes whether or not the page is visible.
  useEffect(() => {
    if (!intentExpiresAt) return;
    const timer = window.setTimeout(
      () => dispatch({ type: "ended", reason: "expired" }),
      Math.max(0, Date.parse(intentExpiresAt) - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [intentExpiresAt]);

  // While the code is up, notice the scan (or a refusal) quickly.
  useEffect(() => {
    if (!redemptionId || !visible || !online) return;
    const run = generation.current;
    let inFlight = false;
    const timer = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      outcomeOf(redemptionId)
        .then((outcome) => {
          if (outcome && run === generation.current) apply(outcome);
        })
        .catch(() => undefined)
        .finally(() => {
          inFlight = false;
        });
    }, REDEMPTION_CHECKED_IN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [apply, online, outcomeOf, redemptionId, visible]);

  // A one-second clock for the countdown while a code is showing.
  useEffect(() => {
    if (state.status !== "showing" || !visible) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [state.status, visible]);

  const cancel = useCallback(async () => {
    const id = redemptionId;
    generation.current += 1;
    if (!id) return;
    // Best effort: an uncancelled intent simply expires and uses nothing.
    await checkinRedemptionApi.cancel(id).catch(() => undefined);
  }, [redemptionId]);

  const passUsable = showing ? now < showing.passReceivedAt + REDEMPTION_PASS_USABLE_MS : false;

  return {
    state,
    redemption,
    now,
    online,
    visible,
    /** False once an unrefreshed pass is too old to scan (offline too long). */
    passUsable,
    start,
    cancel,
  };
}
